import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { PropertyNameArray, SelectedTanksType, TankType } from '../../types/apiResponse/chartTypes'
import { getSchools } from '../../api/school/selectSchool'
import {
  Edge,
  EdgeChange,
  MarkerType,
  Node,
  NodeChange,
  applyEdgeChanges,
  applyNodeChanges,
  getConnectedEdges,
} from 'reactflow'
import { ThemeConfig } from '../../components/selectSchools/config'

const propertiesOrder = [
  'averageWeight',
  'sampleWeight',
  'feedingAmount',
  'count',
  'biomass',
  'nh3no2',
]

type ChartState = {
  groupId: string | null
  groupName: string | null
  selectedSchools: string[]
  properties: PropertyNameArray
  selectedTanks: SelectedTanksType
  nodes: Node[]
  edges: Edge[]
  schools: SelectedTanksType
  preNodes: Node[]
  preEdges: Edge[]
  preGroupName: string | null
  preGroupId: string | null
  preSelectedTanks: SelectedTanksType
  showState: boolean
}

type ChartAction = {
  setGroup: (groupId: string, groupName: string) => void
  setSchools: () => void
  setProperties: (properties: PropertyNameArray) => void
  setSelectedTanks: (tank: TankType) => void
  getSchools: (groupId: string) => void
  reset: () => void
  setSelectedEdges: (id: string | null) => void
  onNodesChange: (change: NodeChange[]) => void
  onEdgesChange: (change: EdgeChange[]) => void
  preSet: () => void
  returnPreSet: () => void
  setShowState: () => void
}

const initialState = {
  groupId: null,
  groupName: null,
  selectedSchools: [],
  properties: [],
  selectedTanks: [],
  nodes: [],
  edges: [],
  schools: [],
  preNodes: [],
  preEdges: [],
  preGroupName: null,
  preGroupId: null,
  preSelectedTanks: [],
  showState: false,
}

const useChartStore = create(
  persist<ChartState & ChartAction>(
    (set, get) => ({
      ...initialState,
      setGroup: (groupId, groupName) => {
        set((state) => {
          state.getSchools(groupId)
          return {
            groupId: groupId,
            groupName: groupName,
            selectedTanks: [],
          }
        })
      },
      setSchools: () => {
        set((state) => {
          if (state.selectedTanks.length !== 0) {
            return {
              schools: state.selectedTanks,
              selectedSchools: state.selectedTanks.map((item) => item.schoolId),
            }
          } else {
            return {
              schools: [],
              selectedSchools: [],
              groupId: null,
              groupName: null,
              nodes: [],
              edges: [],
              preGroupId: null,
              preGroupName: null,
              preNodes: [],
              preEdges: [],
            }
          }
        })
      },
      setProperties: (properties) => {
        set(() => ({
          properties: properties.sort(
            (x, y) => propertiesOrder.indexOf(x) - propertiesOrder.indexOf(y),
          ),
          showState: false,
        }))
      },
      setSelectedTanks: (tank) => {
        set((state) => {
          const isExist = state.selectedTanks.some((item) => item.schoolId === tank.schoolId)
          return {
            selectedTanks: isExist
              ? state.selectedTanks.filter((item) => item.schoolId !== tank.schoolId)
              : [...state.selectedTanks, tank],
            nodes: isExist
              ? get().nodes.map((node: Node) => {
                  if (node.id === tank.schoolId) {
                    node.style = { ...node.style, backgroundColor: ThemeConfig.inactiveNodeColor }
                  }
                  return node
                })
              : get().nodes.map((node: Node) => {
                  if (node.id === tank.schoolId) {
                    node.style = { ...node.style, backgroundColor: ThemeConfig.activeNodeColor }
                  }
                  return node
                }),
          }
        })
      },
      reset: () => {
        set(initialState)
      },
      setSelectedEdges: (id) => {
        set((state) => {
          const selectedEdges = id
            ? getConnectedEdges(state.nodes, state.edges).filter(
                (item) => item.id.split('_')[1] === id,
              )
            : []
          return {
            edges: get().edges.map((edge: Edge) => {
              if (selectedEdges.includes(edge)) {
                edge.style = { strokeWidth: 2, stroke: ThemeConfig.activeEdgeColor }
                edge.markerEnd = {
                  type: MarkerType.ArrowClosed,
                  width: 10,
                  height: 10,
                  color: ThemeConfig.activeEdgeColor,
                }
              } else {
                edge.style = { strokeWidth: 1, stroke: ThemeConfig.inactiveEdgeColor }
                edge.markerEnd = {
                  type: MarkerType.ArrowClosed,
                  width: 20,
                  height: 20,
                  color: ThemeConfig.inactiveEdgeColor,
                }
              }
              return edge
            }),
          }
        })
      },
      getSchools: async (groupId) => {
        const currentData = await getSchools(groupId)

        if (!currentData) return

        set((state) => ({
          nodes: currentData.nodes.map((item: any) => {
            const schoolId = item.school_id || item.schoolId
            const tankName = item.tank_name || item.tankName
            const startDate = item.start_date || item.startDate
            const endDate = item.end_date || item.endDate

            const handles = (item.handles || []).map((h: any) => ({
              handleId: h.handle_id || h.handleId,
              type: h.type,
              position: h.position,
              shift: h.shift,
            }))

            return {
              id: schoolId,
              position: {
                x: ThemeConfig.unitSize * ThemeConfig.widthSize * item.x,
                y: ThemeConfig.heightSize * ThemeConfig.unitSize * ThemeConfig.rawSize * item.y,
              },
              type: 'tankNode',
              data: {
                tankName,
                startDate,
                endDate,
                handles,
                // V1 공식: unitSize * widthSize * (width-1) + unitSize * (widthSize-1)
                width:
                  ThemeConfig.unitSize * ThemeConfig.widthSize * (item.width - 1) +
                  ThemeConfig.unitSize * (ThemeConfig.widthSize - 1),
                height: ThemeConfig.unitSize * ThemeConfig.heightSize,
              },
              style: {
                backgroundColor: state.selectedTanks
                  .map((t) => t.schoolId)
                  .includes(schoolId)
                  ? ThemeConfig.activeNodeColor
                  : ThemeConfig.inactiveNodeColor,
              },
              deletable: false,
            }
          }),
          edges: currentData.edges.map((item: any) => {
            const sourceId = item.source_Id || item.source_id || item.sourceId
            const targetId = item.target_id || item.targetId
            const sourceHandleId = item.source_handle_id || item.sourceHandleId
            const targetHandleId = item.target_handle_id || item.targetHandleId

            return {
              id: `${sourceId}_${targetId}_${sourceHandleId}`,
              source: sourceId,
              target: targetId,
              style: {
                strokeWidth: 1,
                stroke: ThemeConfig.inactiveEdgeColor,
              },
              markerEnd: {
                type: MarkerType.ArrowClosed,
                width: 20,
                height: 20,
                color: ThemeConfig.inactiveEdgeColor,
              },
              sourceHandle: sourceHandleId,
              targetHandle: targetHandleId,
            }
          }),
        }))
      },
      onNodesChange: (changes) => {
        set({
          nodes: applyNodeChanges(changes, get().nodes),
        })
      },
      onEdgesChange: (changes) => {
        set({
          edges: applyEdgeChanges(changes, get().edges),
        })
      },
      preSet: () => {
        set((state) => ({
          preNodes: state.nodes,
          preEdges: state.edges,
          preGroupId: state.groupId,
          preGroupName: state.groupName,
          preSelectedTanks: state.selectedTanks,
          showState: false,
        }))
      },
      returnPreSet: () => {
        set((state) => ({
          nodes: state.preNodes,
          edges: state.preEdges,
          groupId: state.preGroupId,
          groupName: state.preGroupName,
          selectedTanks: state.preSelectedTanks,
        }))
      },
      setShowState: () => {
        set(() => ({ showState: true }))
      },
    }),
    {
      name: 'Chart-storage',
      storage: createJSONStorage(() => localStorage),
    },
  ),
)

export default useChartStore
